import { useContext, useEffect, useRef, useState } from "react"
import { AppContext } from "../App"
import { PiBuildingOfficeBold, PiStudent } from "react-icons/pi";
import { callApi, formatDate, formatMoney, formatMoneyShorten, roundToTwoDecimals } from "../Helpers";
import { FaChalkboardTeacher } from "react-icons/fa";
import { SiGoogleclassroom } from "react-icons/si";
import { IoIosPeople } from "react-icons/io";
import { getInlineLoader } from "../Helpers";
import ClassroomRegistrationForm from "../ones/ClassroomRegistrationForm";
import SubjectRegistrationForm from "../ones/SubjectRegistrationForm";
import ExamCycleCreationForm from "../ones/ExamCycleCreationForm";
import { MdCalculate, MdPrint, MdWarning } from "react-icons/md";
import { useReactToPrint } from "react-to-print";

export default function View({ examCycleUuid }) {

	const appContext = useContext(AppContext);
	const sheetsRef = useRef(null);

	/* Place states here */
	const [ready, setReady] = useState(false);
	const [examCycleData, setExamCycleData] = useState(null);
	const [examCycleResults, setExamCycleResults] = useState(null);
	const [loading, setLoading] = useState(false);
	const [examCycleFor, setExamCycleFor] = useState('classroom');
	const [selectedGradingSystemUuid, setSelectedGradingSystemUuid] = useState("");


	const printSheet = useReactToPrint({
		contentRef: sheetsRef,
		documentTitle: "Results Sheet", // optional
	});
		
	
	async function init() {
		//run initializations here
		setReady(false);
		await Promise.all([
			getExamCycleData(),
			getExamCycleResults(),
			appContext.getAllSubjects(),
		])
		setReady(true);
	}

	async function getExamCycleData() {
		await callApi("get-exam-cycle-data", { uuid: examCycleUuid }).then(response => {
			if (response.status === 1) {
				setExamCycleData(response.data);
			} else {
				appContext.tellError("Could not fetch data, check your connection");
			}
		})
	}

	async function getExamCycleResults() {
		await callApi("get-exam-cycle-results", { uuid: examCycleUuid }).then(response => {
			if (response.status === 1) {
				setExamCycleResults(response.data);
			} else {
				appContext.tellError("Could not fetch data, check your connection");
			}
		})
	}

	function getSubjectName(subjectUuid) {
		let name = '';

		if(appContext.allSubjects) {
			const subject = appContext.allSubjects.find((item, i) => item.uuid === subjectUuid);

			if(subject) {
				name = subject.name;
			}
		}

		return name;
	}

	

	useEffect(() => {
		init();
	}, [])

	useEffect(() => {
		appContext.setShowOverlayLoader(loading);

		return () => {
			appContext.setShowOverlayLoader(false);
		}
	}, [loading])

	useEffect(() => {
		if(examCycleData) {
			if(examCycleData.classroomUuid && examCycleData.streamUuid) {
				setExamCycleFor('stream');
			} else if(examCycleData.classroomUuid && !examCycleData.streamUuid) {
				setExamCycleFor('classroom');
			}
		}
	}, [ examCycleData ])

	if (ready && examCycleData) {
		return (
			<div
				className="container"
			>
				<div className="row">
					<div className="col-12 mb-4">
						<h6 className="main-section-title">
							Exam Cycle Results Processor
						</h6>
						<hr/>
					</div>

					

					<div className="col-md-12 mt-2 mb-2">
						<div className="card">
							<div className="card-body" style={{ overflowX: "auto" }}>

								<h6 className="card-title">Results</h6>
								<hr/>


								<div style={{ overflowX: "auto" }}>

									<div
										style={{
											minWidth: "1600px",
											fontSize: "14px"
										}}
										ref={sheetsRef}
									>

										<div className="row">
											
											<div className="col-md-12 mt-2 mb-2">
												<label className="form-label">Exam Cycle Title</label>
												<h6
													style={{
														fontSize: "18px"
													}}
												>
													{examCycleData.title}
												</h6>
											</div>

											<div className="col-4 mt-2 mb-2">
												<label className="form-label">Status</label>
												<h6
													style={{
														fontSize: "18px"
													}}
												>
													{examCycleData.status}
												</h6>
											</div>

											<div className="col-4 mt-2 mb-2">
												<label className="form-label">Start Date</label>
												<h6
													style={{
														fontSize: "18px"
													}}
												>
													{formatDate(examCycleData.dateStart).substring(0, 10)}
												</h6>
											</div>

											<div className="col-4 mt-2 mb-2">
												<label className="form-label">End Date</label>
												<h6
													style={{
														fontSize: "18px"
													}}
												>
													{formatDate(examCycleData.dateEnd).substring(0, 10)}
												</h6>
											</div>

											<div className="col-4 mt-2 mb-2">
												<label className="form-label">Classroom</label>
												<h6
													style={{
														fontSize: "18px"
													}}
												>
													{examCycleData.classroomData?.name || "N/A"}
												</h6>
											</div>

											<div className="col-4 mt-2 mb-2">
												<label className="form-label">Stream</label>
												<h6
													style={{
														fontSize: "18px"
													}}
												>
													{examCycleData.streamData?.name || "N/A"}
												</h6>
											</div>
											
										</div>


										<table
											className="table table-striped table-bordered mt-5 mb-5"
										>
											<thead>
												<tr>
													<th>Student's Names</th>
													{
														JSON.parse(examCycleData.applicableSubjects).map((item, i) => {
															return (
																<th key={i}>{getSubjectName(item)}</th>
															)
															
														})
													}

													<th>Total</th>
													<th>Average</th>
													<th>Position</th>
													<th>Division</th>
													<th>Points</th>
												</tr>
											</thead>

											{
												(examCycleResults && examCycleResults.length > 0) ?
												<tbody>
													{
														examCycleResults.map((student, studentIndex) => {
															const allStudentResults = JSON.parse(student.examResults);
															const currentCycleStudentResults = allStudentResults.find((item, i) => item.examCycleUuid === examCycleUuid);

															return (
																<tr key={studentIndex}>
																	<td style={{ textTransform: "uppercase" }}>{student.firstName} {student.middleName} {student.lastName}</td>
																	{
																		JSON.parse(examCycleData.applicableSubjects).map((applicableSubject, applicableSubjectIndex) => {
																			const subjectInScope = currentCycleStudentResults.subjects.find((item3, i3) => item3.uuid === applicableSubject);

																			return (
																				<td key={applicableSubjectIndex}>
																					
																					<div className="row">
																						<div 
																							className="col-6 text-center"
																							style={{
																								borderRight: "2px solid black",
																							}}
																						>
																							{subjectInScope?.marks || 'N/A'}
																						</div>
																						<div 
																							className="col-6 text-center"
																							style={{
																								borderLeft: "2px solid black",
																							}}
																						>
																							{ (examCycleData.isProcessed === 'yes') ? (subjectInScope?.grade || 'N/A') : "N/A" }
																						</div>
																					</div>
																				</td>
																			)
																			
																		})
																	}
																	<td>{ (examCycleData.isProcessed === 'yes') ? currentCycleStudentResults.total : "N/A" }</td>
																	<td>{ (examCycleData.isProcessed === 'yes') ? roundToTwoDecimals(currentCycleStudentResults.average) : "N/A"}</td>
																	<td>{ (examCycleData.isProcessed === 'yes') ? currentCycleStudentResults.position : "N/A" }</td>
																	<td>{ (examCycleData.isProcessed === 'yes') ? (currentCycleStudentResults.division || "N/A") : "N/A" }</td>
																	<td>{ (examCycleData.isProcessed === 'yes') ? (currentCycleStudentResults.points || "N/A") : "N/A"}</td>
																</tr>
															)
														})
													}
												</tbody>
												:""
											}
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>


				</div>

			</div>
		)
	} else {
		return (
			<div
				className="container mSupportLoading"
			>
				{getInlineLoader()}
			</div>
		);
	}


}